import { apiService } from './api'
import { adminAuthService } from './adminAuth'

/**
 * 백업 관리 API.
 *
 * 경로 앞에 /api 를 직접 붙이지 않는다. 공용 axios 클라이언트의 baseURL 이
 * 호스트에 따라 정해 준다. 화면에서 fetch('/admin/backup/...') 를 직접
 * 부르면 nginx 가 /api/ 만 백엔드로 넘기므로 정적 서버의 SPA fallback 에
 * 걸려 HTML 이 돌아온다.
 */

/** 백엔드 BackupService.listBackups + 라우트의 sizeFormatted 와 같은 모양 */
export interface BackupFile {
  id: string
  filename: string
  size: number
  sizeFormatted: string
  createdAt: string
  type: 'hourly' | 'manual' | 'daily' | 'weekly' | 'monthly' | 'pre-restore'
  status: 'success' | 'failed' | 'in_progress'
}

export interface BackupStatus {
  isScheduled: boolean
  lastBackup?: BackupFile
  nextScheduled: {
    daily: string
    weekly: string
    monthly: string
  }
}

export interface RestoreOptions {
  [key: string]: unknown
}

/** 복원 결과. 백엔드가 안전 백업·검증을 수행했을 때만 채워진다 */
export interface RestoreResult {
  preBackupCreated?: string
  verification?: {
    success: boolean
    score?: number
  }
}

function authConfig() {
  const token = adminAuthService.getToken()
  if (!token) {
    throw new Error('No admin token available')
  }
  return { headers: { Authorization: `Bearer ${token}` } }
}

/** success:false 를 서버 메시지와 함께 예외로 올린다 */
function unwrap<T>(body: any, pick: (body: any) => T, fallback: string): T {
  if (!body?.success) {
    throw new Error(body?.message || fallback)
  }
  return pick(body)
}

export const adminBackupApi = {
  async list(): Promise<BackupFile[]> {
    const { data } = await apiService.client.get(
      '/admin/backup/list',
      authConfig()
    )
    return unwrap(data, b => b.backups ?? [], '백업 목록 조회 실패')
  },

  async status(): Promise<BackupStatus> {
    const { data } = await apiService.client.get(
      '/admin/backup/status',
      authConfig()
    )
    return unwrap(data, b => b.status ?? {}, '백업 상태 조회 실패')
  },

  async create(type: string = 'manual'): Promise<void> {
    const { data } = await apiService.client.post(
      '/admin/backup/create',
      { type },
      authConfig()
    )
    unwrap(data, () => undefined, '백업 생성 실패')
  },

  async remove(filename: string): Promise<void> {
    const { data } = await apiService.client.delete(
      `/admin/backup/${encodeURIComponent(filename)}`,
      authConfig()
    )
    unwrap(data, () => undefined, '백업 삭제 실패')
  },

  async restore(
    filename: string,
    options: RestoreOptions
  ): Promise<RestoreResult> {
    const { data } = await apiService.client.post(
      '/admin/backup/restore',
      { filename, options },
      authConfig()
    )
    return unwrap(data, b => b.result ?? {}, '복원에 실패했습니다')
  },
}

export default adminBackupApi
